
/**
 * Team Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { TeamFormSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class TeamService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                message: body.message || 'Team listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch team list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch team');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        // Rule: Validation at boundary
        const validation = TeamFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;

        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Team oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create team');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        // Validate before update
        const validation = TeamFormSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;

        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Team güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update team');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'Team silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete team');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            // Use optimized batch delete endpoint
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            // Fallback to sequential delete if batch endpoint fails
            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} öğe silindi`
            };
        }
    }

    /**
     * Get teams for dropdown selection
     * @returns {Promise<Object>}
     */
    async getDropdownParams() {
        logDropdownParamsInvocation('TeamService');
        try {
            const response = await this.api.getAll({ pageSize: 1000 });
            const dropdownData = (response.data || []).map(d => ({
                value: d._id || d.id,
                label: d.name || d.code || ''
            }));
            return {
                success: true,
                data: dropdownData,
                message: 'Dropdown verisi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch dropdown data');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }

    async getAssignments(id, params = {}) {
        try {
            const response = await this.api.getAssignments(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch team assignments');
        }
    }

    async createAssignment(id, body) {
        try {
            const response = await this.api.createAssignment(id, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to link team');
        }
    }

    async updateAssignment(id, assignmentId, body) {
        try {
            const response = await this.api.updateAssignment(id, assignmentId, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update assignment');
        }
    }

    async deleteAssignment(id, assignmentId) {
        try {
            const response = await this.api.deleteAssignment(id, assignmentId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to unlink team');
        }
    }

    async getDutyProfiles(id, params = {}) {
        try {
            const response = await this.api.getDutyProfiles(id, params);
            return { success: true, data: response.data || [], pagination: response.pagination || {} };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch duty profiles');
        }
    }

    async createDutyProfile(id, body) {
        try {
            const response = await this.api.createDutyProfile(id, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to create duty profile');
        }
    }

    async updateDutyProfile(id, profileId, body) {
        try {
            const response = await this.api.updateDutyProfile(id, profileId, body);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to update duty profile');
        }
    }

    async deleteDutyProfile(id, profileId) {
        try {
            const response = await this.api.deleteDutyProfile(id, profileId);
            return { success: true, data: response.data, message: response.message };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete duty profile');
        }
    }
}
